import { useCallback, useEffect, useRef, useState } from 'react'
import { MAX_DICTATION_SECONDS } from '../lib/voice'
import { transcribeAudio } from '../services/voiceService'

interface VoiceDictationOptions {
  language?: string
  maxDurationSeconds?: number
  onError?: (error: Error) => void
  onTranscript: (transcript: string) => void
}

const SUPPORTED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus'
]

const getSupportedMimeType = () => {
  if (typeof MediaRecorder === 'undefined') {
    return undefined
  }

  return SUPPORTED_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
}

export function useVoiceDictation({
  language,
  maxDurationSeconds = MAX_DICTATION_SECONDS,
  onError,
  onTranscript
}: VoiceDictationOptions) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const shouldTranscribeRef = useRef(true)
  const timeoutRef = useRef<number | null>(null)
  const intervalRef = useRef<number | null>(null)
  const latestHandlersRef = useRef({ language, onError, onTranscript })

  useEffect(() => {
    latestHandlersRef.current = { language, onError, onTranscript }
  }, [language, onError, onTranscript])

  const isSupported =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const finishRecording = useCallback(async (mimeType: string) => {
    const shouldTranscribe = shouldTranscribeRef.current
    const audioChunks = [...chunksRef.current]

    clearTimers()
    stopStream()
    recorderRef.current = null
    chunksRef.current = []
    setElapsedSeconds(0)

    if (!shouldTranscribe) {
      return
    }

    const audioBlob = new Blob(audioChunks, {
      type: mimeType || 'audio/webm'
    })

    setIsTranscribing(true)

    try {
      const transcript = await transcribeAudio(audioBlob, {
        language: latestHandlersRef.current.language,
        mimeType: audioBlob.type
      })

      latestHandlersRef.current.onTranscript(transcript)
    } catch (error) {
      const resolvedError = error instanceof Error ? error : new Error('voice_transcription_failed')
      latestHandlersRef.current.onError?.(resolvedError)
    } finally {
      setIsTranscribing(false)
    }
  }, [clearTimers, stopStream])

  const stopRecording = useCallback(() => {
    if (!recorderRef.current || recorderRef.current.state === 'inactive') {
      return
    }

    shouldTranscribeRef.current = true
    recorderRef.current.stop()
    setIsRecording(false)
  }, [])

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      latestHandlersRef.current.onError?.(new Error('voice_unsupported'))
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedMimeType()
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)

      streamRef.current = stream
      recorderRef.current = recorder
      chunksRef.current = []
      shouldTranscribeRef.current = true

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      recorder.onerror = () => {
        shouldTranscribeRef.current = false
        clearTimers()
        stopStream()
        recorderRef.current = null
        chunksRef.current = []
        setIsRecording(false)
        setElapsedSeconds(0)
        latestHandlersRef.current.onError?.(new Error('voice_recording_failed'))
      }

      recorder.onstop = () => {
        setIsRecording(false)
        void finishRecording(recorder.mimeType || mimeType || 'audio/webm')
      }

      recorder.start()
      setElapsedSeconds(0)
      setIsRecording(true)

      intervalRef.current = window.setInterval(() => {
        setElapsedSeconds((currentValue) => currentValue + 1)
      }, 1000)

      timeoutRef.current = window.setTimeout(() => {
        stopRecording()
      }, maxDurationSeconds * 1000)
    } catch (error) {
      const permissionDenied =
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')

      latestHandlersRef.current.onError?.(
        new Error(permissionDenied ? 'voice_permission_denied' : 'voice_recording_failed')
      )
    }
  }, [clearTimers, finishRecording, isSupported, maxDurationSeconds, stopRecording, stopStream])

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording()
      return
    }

    if (isTranscribing) {
      return
    }

    await startRecording()
  }, [isRecording, isTranscribing, startRecording, stopRecording])

  useEffect(() => {
    return () => {
      shouldTranscribeRef.current = false

      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }

      clearTimers()
      stopStream()
    }
  }, [clearTimers, stopStream])

  return {
    elapsedSeconds,
    isRecording,
    isSupported,
    isTranscribing,
    toggleRecording
  }
}
